import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { checkAuth, logout, AuthStatus, handleTokenFromUrl, retryAuth, getRateLimitInfo } from '../services/auth'
import { User } from '../services/userApi'
import { getAuthCache, setAuthCache, clearAuthCache } from '../utils/authCache'

interface AuthContextType {
  isAuthenticated: boolean
  user?: User
  loading: boolean
  error?: string
  isRateLimited: boolean
  rateLimitResetTime?: Date
  logout: () => Promise<void>
  refresh: () => Promise<void>
  retryAuth: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

// BroadcastChannel for cross-tab auth synchronization (if supported)
const authChannel = typeof BroadcastChannel !== 'undefined' 
  ? new BroadcastChannel('auth_events') 
  : null

export function AuthProvider({ children }: AuthProviderProps) {
  // Check for cached auth state to avoid initial loading flash
  const cachedAuth = getAuthCache()
  
  const [authState, setAuthState] = useState<{
    isAuthenticated: boolean
    user?: User
    loading: boolean
    error?: string
    isRateLimited: boolean
    rateLimitResetTime?: Date
  }>({
    isAuthenticated: cachedAuth?.isAuthenticated || false,
    user: cachedAuth?.user || undefined,
    // Only skip loading if we have valid cache (same tab refresh)
    // For new tabs, we need to wait for auth check before redirecting
    loading: !cachedAuth,
    isRateLimited: false,
  })

  const refreshAuth = async (skipLoading = false) => {
    try {
      // Don't show loading for background refreshes
      if (!skipLoading) {
        setAuthState(prev => ({ ...prev, error: undefined }))
      }
      
      const status: AuthStatus = await checkAuth()
      
      // Update cache with new auth state (only if not rate limited)
      if (!status.isRateLimited) {
        setAuthCache(status.isAuthenticated, status.user)
      }
      
      setAuthState(prev => {
        // Broadcast auth change to other tabs if auth state changed and not rate limited
        if (authChannel && !status.isRateLimited && status.isAuthenticated !== prev.isAuthenticated) {
          authChannel.postMessage({ type: 'AUTH_CHANGED' })
        }
        
        return {
          isAuthenticated: status.isAuthenticated,
          user: status.user,
          loading: false,
          error: status.error,
          isRateLimited: status.isRateLimited || false,
          rateLimitResetTime: status.rateLimitResetTime,
        }
      })
    } catch (error) {
      console.error('Auth refresh failed:', error)
      // Clear cache on auth failure (but not if it's a rate limit issue)
      const rateLimitInfo = getRateLimitInfo()
      if (!rateLimitInfo.isRateLimited) {
        clearAuthCache()
      }
      
      setAuthState({
        isAuthenticated: false,
        loading: false,
        error: 'Failed to check authentication',
        isRateLimited: rateLimitInfo.isRateLimited,
      })
    }
  }

  const handleRetryAuth = async () => {
    console.log('🔄 Manual retry auth requested from context')
    try {
      const status: AuthStatus = await retryAuth()
      
      // Update cache with new auth state (only if not rate limited)
      if (!status.isRateLimited) {
        setAuthCache(status.isAuthenticated, status.user)
      }
      
      setAuthState(prev => {
        // Broadcast auth change to other tabs if auth state changed and not rate limited
        if (authChannel && !status.isRateLimited && status.isAuthenticated !== prev.isAuthenticated) {
          authChannel.postMessage({ type: 'AUTH_CHANGED' })
        }
        
        return {
          isAuthenticated: status.isAuthenticated,
          user: status.user,
          loading: false,
          error: status.error,
          isRateLimited: status.isRateLimited || false,
          rateLimitResetTime: status.rateLimitResetTime,
        }
      })
    } catch (error) {
      console.error('Manual retry auth failed:', error)
      const rateLimitInfo = getRateLimitInfo()
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to retry authentication',
        isRateLimited: rateLimitInfo.isRateLimited,
      }))
    }
  }

  const handleLogout = async () => {
    try {
      // Clear cache before logout
      clearAuthCache()
      
      // Broadcast logout to other tabs
      if (authChannel) {
        authChannel.postMessage({ type: 'AUTH_CHANGED' })
      }
      
      await logout()
      // logout() redirects, so this won't be reached
    } catch (error) {
      console.error('Logout failed:', error)
      // Clear cache and force refresh to clear state
      clearAuthCache()
      await refreshAuth()
    }
  }

  useEffect(() => {
    const initializeAuth = async () => {
      console.log('🚀 Initializing auth context')
      
      // Handle token from URL fragment if present (after OAuth redirect)
      const tokenExtracted = handleTokenFromUrl()
      console.log('🔍 Token extracted from URL:', tokenExtracted)
      
      // If token was just extracted, wait a moment for cookie to be set
      // and for any browser processing to complete
      if (tokenExtracted) {
        console.log('⏰ Waiting for auth processing to complete...')
        await new Promise(resolve => setTimeout(resolve, 300))
      }
      
      // Then check authentication status
      console.log('🔍 Starting auth refresh...')
      await refreshAuth()
    }
    
    // Add error handling for initialization
    initializeAuth().catch(error => {
      console.error('❌ Auth initialization failed:', error)
      setAuthState({
        isAuthenticated: false,
        loading: false,
        error: 'Failed to initialize authentication'
      })
    })
    
    // Listen for visibility changes to revalidate auth
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const rateLimitInfo = getRateLimitInfo()
        if (rateLimitInfo.isRateLimited && !rateLimitInfo.canRetryNow) {
          console.log('⏳ Tab became visible but rate limited, skipping auth refresh')
          return
        }
        console.log('🔄 Tab became visible, revalidating auth...')
        refreshAuth(true) // Skip loading state for background refresh
      }
    }
    
    // Listen for auth changes from other tabs
    const handleAuthMessage = (event: MessageEvent) => {
      if (event.data?.type === 'AUTH_CHANGED') {
        const rateLimitInfo = getRateLimitInfo()
        if (rateLimitInfo.isRateLimited && !rateLimitInfo.canRetryNow) {
          console.log('📡 Auth change detected but rate limited, skipping refresh')
          return
        }
        console.log('📡 Auth change detected from another tab, refreshing...')
        refreshAuth(true) // Skip loading state for background refresh
      }
    }
    
    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange)
    if (authChannel) {
      authChannel.addEventListener('message', handleAuthMessage)
    }
    
    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      if (authChannel) {
        authChannel.removeEventListener('message', handleAuthMessage)
      }
    }
  }, [])

  const contextValue: AuthContextType = {
    isAuthenticated: authState.isAuthenticated,
    user: authState.user,
    loading: authState.loading,
    error: authState.error,
    isRateLimited: authState.isRateLimited,
    rateLimitResetTime: authState.rateLimitResetTime,
    logout: handleLogout,
    refresh: refreshAuth,
    retryAuth: handleRetryAuth,
  }

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}